'use client';

/**
 * useUnifiedNotifications Hook
 *
 * Single entry point for notifications in the application.
 *
 * Features:
 * - Scope-aware persistence: page-only toasts, session notifications, or global DB notifications
 * - Deduplication to prevent spam from rapid actions
 * - Rate limiting with priority-based bypass
 * - Toast display for immediate feedback
 * - Categories enable filtering in notification panel
 *
 * Usage:
 *   const { notify } = useUnifiedNotifications();
 *
 *   // Success notification (persists to DB)
 *   notify.success('Recording started', { category: 'call' });
 *
 *   // Error notification (persists to DB)
 *   notify.error('Failed to save recording');
 *
 *   // Full control
 *   notify({
 *     title: 'Analysis Complete',
 *     category: 'ai_analysis_complete',
 *     actionUrl: '/calls/123',
 *   });
 */

import { useCallback, useRef } from 'react';
import { usePathname } from 'next/navigation';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { addNotification, selectPreferences } from '@/store/slices/notificationsSlice';
import type { Notification } from '@/store/slices/notificationsTypes';
import {
  NotifyOptions,
  ShorthandOptions,
  NotificationPriority,
  ToastStyle,
  getNotificationRule,
  getDedupeWindow,
  getEffectiveScope,
  shouldStore,
  TOAST_DURATIONS,
  getDefaultBehavior,
  computeExpiryDate,
} from '@/lib/notifications/types';
import { showUnifiedToast } from '@/lib/notifications/show-unified-toast';
import type { ToastVariant } from '@/components/ui/UnifiedToast';
import { checkDedupe, getDeduplicator } from '@/lib/notifications/deduplication';
import { checkRateLimit, recordNotification, enqueueNotification } from '@/lib/notifications/rateLimiter';
import { addSessionNotification } from '@/lib/notifications/sessionStore';
import logger from '@/lib/logger';

// Use Next.js API route for notifications (not AI Engine)
const NOTIFICATIONS_API = '/api/notifications';

/**
 * Map ToastStyle to ToastVariant for the unified component
 */
const STYLE_TO_VARIANT: Record<ToastStyle, ToastVariant> = {
  success: 'success',
  error: 'error',
  info: 'info',
  warning: 'warning',
  loading: 'loading',
};

/**
 * Generate a unique ID for notifications
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * The notify function interface with shorthand methods
 */
export interface NotifyFunction {
  (options: NotifyOptions): string | null;

  // Shorthand methods - return toast ID for potential dismissal
  // Persistence is fire-and-forget (non-blocking)
  success: (title: string, options?: ShorthandOptions) => string | null;
  error: (title: string, options?: ShorthandOptions) => string | null;
  info: (title: string, options?: ShorthandOptions) => string | null;
  warning: (title: string, options?: ShorthandOptions) => string | null;
  loading: (title: string, options?: ShorthandOptions) => string;

  // Control methods
  dismiss: (toastId: string) => void;
  dismissAll: () => void;

  // Promise-based toast (for async operations)
  promise: <T>(
    promise: Promise<T>,
    msgs: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: Error) => string);
    },
    options?: ShorthandOptions
  ) => Promise<T>;
}

export function useUnifiedNotifications() {
  // Delivery preferences, written by /settings/ui/notifications.
  //
  // notify() never read them, so the per-category and per-channel toggles on
  // that page were decorative: a user turning "Reports" off, or disabling
  // In-App Notifications entirely, kept receiving both toasts and panel
  // entries. The hook that owns these preferences exposes isChannelEnabled()
  // and getPreference(), and neither had a single caller anywhere.
  const notificationPreferences = useAppSelector(selectPreferences);
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const toastIdsRef = useRef<Set<string>>(new Set());

  /**
   * Show a toast notification via the unified toast component
   */
  const showToast = useCallback((
    title: string,
    style: ToastStyle,
    priority: NotificationPriority,
    duration?: number,
    toasterId?: string
  ): string => {
    const toastDuration = duration || TOAST_DURATIONS[priority];
    const variant = STYLE_TO_VARIANT[style] || 'info';

    const toastId = showUnifiedToast({
      variant,
      title,
      duration: toastDuration,
      toasterId,
    });

    toastIdsRef.current.add(toastId);
    return toastId;
  }, []);

  /**
   * Create a persistent notification via API
   */
  const createPersistentNotification = useCallback(async (
    options: NotifyOptions
  ): Promise<Notification | null> => {
    try {
      const priority = options.priority || 'normal';
      // Determine behavior: explicit > category/priority default > 'actionable'
      const behavior = options.behavior
        || getDefaultBehavior(options.category, priority);

      const response = await fetch(NOTIFICATIONS_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          category: options.category,
          priority,
          title: options.title,
          body: options.body,
          icon: options.icon,
          action_url: options.actionUrl,
          action_label: options.actionLabel,
          metadata: options.metadata,
          behavior,
          expires_at: computeExpiryDate(options.category),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.warn('UnifiedNotifications', 'Failed to create notification', { status: response.status, errorText });
        return null;
      }

      const data = await response.json();

      // Transform to match frontend types (API returns snake_case)
      const notification: Notification = {
        id: data.id,
        userId: data.user_id,
        orgUnitId: data.org_unit_id,
        category: data.category,
        priority: data.priority,
        behavior: data.behavior || 'actionable',
        title: data.title,
        body: data.body,
        icon: data.icon,
        actionUrl: data.action_url,
        actionLabel: data.action_label,
        metadata: data.metadata || {},
        isRead: data.is_read || false,
        readAt: data.read_at,
        isArchived: data.is_archived || false,
        deliveredInapp: data.delivered_inapp ?? true,
        deliveredPush: data.delivered_push || false,
        deliveredEmail: data.delivered_email || false,
        createdAt: data.created_at,
        expiresAt: data.expires_at,
      };

      // Add to Redux store (Supabase realtime might also catch this, but ensure immediate update)
      dispatch(addNotification(notification));

      return notification;
    } catch (error) {
      // Log but don't throw - this is fire-and-forget
      logger.warn('UnifiedNotifications', 'API error', { error: (error as Error).message });
      return null;
    }
  }, [dispatch]);

  /**
   * Create a session-scoped notification (stored in sessionStorage)
   */
  const createSessionNotification = useCallback((options: NotifyOptions): void => {
    if (!options.session) {
      logger.warn('UnifiedNotifications', 'Session notification requires session option');
      return;
    }

    addSessionNotification({
      title: options.title,
      body: options.body,
      category: options.category,
      priority: options.priority || 'normal',
      toastStyle: options.toastStyle || 'info',
      session: options.session,
      actionUrl: options.actionUrl,
      actionLabel: options.actionLabel,
      metadata: options.metadata,
    });
  }, []);

  const createLocalHeaderNotification = useCallback((options: NotifyOptions): void => {
    const priority = options.priority || 'normal';
    const behavior = options.behavior || getDefaultBehavior(options.category, priority);
    const createdAt = new Date().toISOString();
    const isRead = behavior === 'informational';

    dispatch(addNotification({
      id: `local-${generateId()}`,
      userId: 'local',
      category: options.category,
      priority,
      behavior,
      title: options.title,
      body: options.body || '',
      icon: options.icon,
      actionUrl: options.actionUrl || pathname || undefined,
      actionLabel: options.actionLabel,
      metadata: {
        ...(options.metadata || {}),
        local: true,
        sourcePage: pathname,
      },
      sourcePage: pathname,
      isRead,
      readAt: isRead ? createdAt : undefined,
      isArchived: false,
      deliveredInapp: true,
      deliveredPush: false,
      deliveredEmail: false,
      createdAt,
      expiresAt: undefined,
    }));
  }, [dispatch, pathname]);

  const persistScopedNotification = useCallback((options: NotifyOptions): void => {
    const effectiveScope = getEffectiveScope(options);

    if (!shouldStore(effectiveScope)) {
      if (pathname?.startsWith('/calls/')) {
        createLocalHeaderNotification({
          ...options,
          category: options.category || 'call',
        });
      }
      return;
    }

    if (effectiveScope === 'session') {
      createSessionNotification(options);
      return;
    }

    createPersistentNotification(options).catch((err) => {
      logger.warn('UnifiedNotifications', 'Failed to persist notification', { error: err });
    });
  }, [createLocalHeaderNotification, createPersistentNotification, createSessionNotification, pathname]);

  /**
   * Main notify function with flood protection
   */
  const notify = useCallback((options: NotifyOptions): string | null => {
    const {
      title,
      body,
      category,
      priority = 'normal',
      dedupeKey,
      dedupeWindowMs,
      showToast: shouldShowToast = true,
      toastDuration,
      toastStyle = 'info',
    } = options;

    // Respect the user's delivery preferences BEFORE any other gate.
    //
    // An ABSENT preference means allow: a user who has never opened the
    // settings page has no rows, and defaulting those to "off" would silence
    // every notification. Only an explicit opt-out suppresses.
    const categoryPreference = notificationPreferences.find(
      (p) => p.category === category,
    );
    if (categoryPreference) {
      if (categoryPreference.enabled === false) {
        return null;
      }
      if (categoryPreference.inappEnabled === false) {
        return null;
      }
    }

    // Determine actual dedup window
    const actualDedupeWindow = dedupeWindowMs || getDedupeWindow(category);

    // Check deduplication
    if (dedupeKey) {
      const dedupeResult = checkDedupe(dedupeKey, actualDedupeWindow);
      if (!dedupeResult.show) {
        // Suppressed due to deduplication
        return null;
      }

      // Handle grouped notification
      if (dedupeResult.isGrouped && dedupeResult.groupCount && dedupeResult.groupCount > 1) {
        // Modify title to show count
        const groupedTitle = `${title} (${dedupeResult.groupCount} times)`;
        return notify({
          ...options,
          title: groupedTitle,
          dedupeKey: undefined, // Don't dedupe the grouped notification
        });
      }
    }

    // Check rate limit
    const rateLimitResult = checkRateLimit(category, priority);
    if (!rateLimitResult.allowed) {
      // Queue for later delivery
      enqueueNotification(category, priority, () => {
        notify(options);
      });
      return null;
    }

    // Record the notification
    recordNotification(category);

    let toastId: string | null = null;

    // Show toast if enabled
    if (shouldShowToast) {
      const displayText = body ? `${title}: ${body}` : title;
      toastId = showToast(displayText, toastStyle, priority, toastDuration, options.toasterId);
    }

    persistScopedNotification(options);

    return toastId;
  }, [showToast, persistScopedNotification, notificationPreferences]) as NotifyFunction;

  /**
   * Auto-dedupe helper: suppress duplicate messages with the same title within a window.
   * Callers can override with an explicit dedupeKey in options.
   * Returns true if the notification should be suppressed.
   */
  const shouldSuppressDuplicate = (style: string, title: string, options?: ShorthandOptions): boolean => {
    const key = options?.dedupeKey || `shorthand:${style}:${title}`;
    const window = options?.dedupeWindowMs || 10000; // 10s default window
    const result = checkDedupe(key, window);
    return !result.show;
  };

  /**
   * Shorthand: Success notification (toast only by default, stores according to scope)
   */
  notify.success = (title: string, options?: ShorthandOptions): string | null => {
    if (shouldSuppressDuplicate('success', title, options)) return null;
    const rule = getNotificationRule('save.success');
    const toastId = showToast(title, 'success', options?.priority || rule.priority, options?.toastDuration, options?.toasterId);

    persistScopedNotification({
      title,
      category: options?.category || 'system',
      priority: options?.priority || rule.priority,
      ...options,
    });

    return toastId;
  };

  /**
   * Shorthand: Error notification (always persists — errors are important)
   */
  notify.error = (title: string, options?: ShorthandOptions): string | null => {
    if (shouldSuppressDuplicate('error', title, options)) return null;
    const rule = getNotificationRule('api.error');
    const toastId = showToast(title, 'error', options?.priority || rule.priority, options?.toastDuration, options?.toasterId);

    // Errors always persist to DB
    createPersistentNotification({
      title,
      category: options?.category || 'alert',
      priority: options?.priority || rule.priority,
      behavior: 'actionable',
      ...options,
    }).catch((err) => {
      logger.warn('UnifiedNotifications', 'Failed to persist notification', { error: err });
    });

    return toastId;
  };

  /**
   * Shorthand: Info notification (toast only by default, stores according to scope)
   */
  notify.info = (title: string, options?: ShorthandOptions): string | null => {
    if (shouldSuppressDuplicate('info', title, options)) return null;
    const rule = getNotificationRule('mode.changed');
    const toastId = showToast(title, 'info', options?.priority || rule.priority, options?.toastDuration, options?.toasterId);

    persistScopedNotification({
      title,
      category: options?.category || 'system',
      priority: options?.priority || rule.priority,
      ...options,
    });

    return toastId;
  };

  /**
   * Shorthand: Warning notification (toast only by default, stores according to scope)
   */
  notify.warning = (title: string, options?: ShorthandOptions): string | null => {
    if (shouldSuppressDuplicate('warning', title, options)) return null;
    const toastId = showToast(title, 'warning', options?.priority || 'normal', options?.toastDuration, options?.toasterId);

    persistScopedNotification({
      title,
      category: options?.category || 'alert',
      priority: options?.priority || 'normal',
      ...options,
    });

    return toastId;
  };

  /**
   * Shorthand: Loading notification (for async operations)
   */
  notify.loading = (title: string, options?: ShorthandOptions): string => {
    return showToast(title, 'loading', 'normal', undefined, options?.toasterId);
  };

  /**
   * Dismiss a specific toast
   */
  notify.dismiss = (toastId: string): void => {
    toast.dismiss(toastId);
    toastIdsRef.current.delete(toastId);
  };

  /**
   * Dismiss all toasts
   */
  notify.dismissAll = (): void => {
    toast.dismiss();
    toastIdsRef.current.clear();
  };

  /**
   * Promise-based notification for async operations
   * Errors always persist; success stores according to the requested scope.
   * Uses manual loading→dismiss→success/error since toast.promise() doesn't support custom rendering.
   */
  notify.promise = async <T>(
    promiseValue: Promise<T>,
    msgs: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((err: Error) => string);
    },
    options?: ShorthandOptions
  ): Promise<T> => {
    const loadingId = showUnifiedToast({
      variant: 'loading',
      title: msgs.loading,
    });
    toastIdsRef.current.add(loadingId);

    try {
      const result = await promiseValue;
      toast.dismiss(loadingId);
      toastIdsRef.current.delete(loadingId);

      const successMsg = typeof msgs.success === 'function' ? msgs.success(result) : msgs.success;
      showUnifiedToast({
        variant: 'success',
        title: successMsg,
        duration: options?.toastDuration,
      });

      persistScopedNotification({
        title: successMsg,
        category: options?.category || 'system',
        priority: options?.priority || 'normal',
        ...options,
      });

      return result;
    } catch (err) {
      toast.dismiss(loadingId);
      toastIdsRef.current.delete(loadingId);

      const errorMsg = typeof msgs.error === 'function' ? msgs.error(err as Error) : msgs.error;
      showUnifiedToast({
        variant: 'error',
        title: errorMsg,
        duration: options?.toastDuration,
      });

      // Errors always persist
      createPersistentNotification({
        title: errorMsg,
        category: options?.category || 'alert',
        priority: 'high',
        body: (err as Error).message,
        behavior: 'actionable',
        ...options,
      });

      throw err;
    }
  };

  return { notify };
}

export default useUnifiedNotifications;
